import { Injectable } from '@angular/core';
import { OfflineStateService } from './offline-state.service';
import { TerminalIdentityService } from '@stockflow/offline-core';

/**
 * Generates unique offline document numbers using concatenation.
 * Format: {PREFIX}-{TERMINAL_ID}-{USER_ID}-{YYYYMMDD}-{SEQ}
 * Guarantees uniqueness without server contact.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSequenceService {
  constructor(
    private offlineState: OfflineStateService,
    private terminalIdentity: TerminalIdentityService,
  ) {}

  // `terminalId = 'POS001'` en dur et son `setTerminalId` jamais appele ont ete
  // retires : tous les postes produisaient des numeros identiques. L'identite
  // vient desormais de TerminalIdentityService, qui garantit qu'elle est
  // distincte et persistante. Voir rehaussement/audit-synchronisation, ecart Y4.

  async generateNumber(prefix: string, userId: string): Promise<string> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Database not initialized');

    // exiger() jette si l'identite n'est pas resolue : mieux vaut refuser
    // d'emettre un document que d'en emettre un dont le numero peut entrer en
    // collision avec celui d'une autre caisse.
    const terminalId = this.terminalIdentity.exiger();

    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const compositeId = `${prefix}_${terminalId}_${userId}_${today}`;

    const existing = await db.offline_sequences.findOne(compositeId).exec();
    let nextSeq = 1;

    if (existing) {
      nextSeq = (existing.toJSON() as { lastSequence: number }).lastSequence + 1;
      await existing.incrementalPatch({ lastSequence: nextSeq });
    } else {
      await db.offline_sequences.insert({
        id: compositeId,
        entityType: prefix,
        terminalId,
        userId,
        date: today,
        lastSequence: nextSeq,
      });
    }

    const seq = String(nextSeq).padStart(4, '0');
    return `${prefix}-${terminalId}-${userId}-${today}-${seq}`;
  }

  async generateInvoiceNumber(userId: string): Promise<string> {
    return this.generateNumber('FAC', userId);
  }

  async generatePurchaseOrderNumber(userId: string): Promise<string> {
    return this.generateNumber('PO', userId);
  }

  async generateJournalEntryNumber(userId: string): Promise<string> {
    return this.generateNumber('JE', userId);
  }

  async generateStockMovementNumber(userId: string): Promise<string> {
    return this.generateNumber('SM', userId);
  }

  async generatePayRunNumber(userId: string): Promise<string> {
    return this.generateNumber('PAY', userId);
  }

  async generateLeaveRequestNumber(userId: string): Promise<string> {
    return this.generateNumber('LR', userId);
  }
}
